import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
  Stack,
  Typography,
  Zoom,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axiosInstance from '../../api/axiosConfig';
import swimService from '../../api/services/swimService';
import consentService from '../../api/services/consentService';
import { EnrollSwimmerResponse } from '../../types/swim';
import { ConsentMeta } from '../../types/consent';
import { useIsMobile } from '../../hooks/useIsMobile';

interface SwimProgramLite {
  id: string;
  name: string;
  description: string | null;
  price_member: number;
  price_non_member: number | null;
  start_date: string;
  end_date: string;
  required_consents: string[] | null;
  target_audience?: 'kids' | 'adult' | 'all' | string;
}

interface HouseholdSwimmer {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  member_type: string;
  relationship_to_primary: string | null;
  is_active: boolean;
  is_member: boolean;  // policy flag for pricing (a non-member adult enrolls via member_id)
  // 'dependent'            → submitted as dependent_id (kids backed by ChildDataConsent).
  // 'member'               → submitted as member_id (adults; the caller themselves or
  //                          another PRIMARY/SECONDARY in the household).
  // 'non_member_dependent' → submitted as non_member_dependent_id (a NonMemberFamily
  //                          row belonging to a non-member parent).
  kind: 'dependent' | 'member' | 'non_member_dependent';
}

interface Props {
  program: SwimProgramLite;
  onClose: () => void;
  onEnrolled: () => void;
}

const STEPS = ['Pick swimmers', 'Review consents', 'Confirm & submit'];

interface PerKidResult {
  dependentId: string;
  swimmerName: string;
  status: 'ok' | 'error';
  response?: EnrollSwimmerResponse;
  error?: string;
}

function ageFromDob(dob: string | null): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a -= 1;
  return a;
}

const SwimEnrollmentWizard: React.FC<Props> = ({ program, onClose, onEnrolled }) => {
  const isMobile = useIsMobile();
  const audience = (program.target_audience || 'kids').toLowerCase();
  const allowKids = audience === 'kids' || audience === 'all';
  const allowAdults = audience === 'adult' || audience === 'all';

  const [step, setStep] = useState(0);
  const [familyMembers, setFamilyMembers] = useState<HouseholdSwimmer[]>([]);
  const [loadingFamily, setLoadingFamily] = useState(false);
  const [selectedKidIds, setSelectedKidIds] = useState<Set<string>>(new Set());
  const [consentChecks, setConsentChecks] = useState<Record<string, boolean>>({});
  const [consentMeta, setConsentMeta] = useState<Map<string, ConsentMeta>>(new Map());
  const [infoConsent, setInfoConsent] = useState<ConsentMeta | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<PerKidResult[] | null>(null);

  const requiredConsents = program.required_consents ?? [];

  // Price each selected swimmer by membership, matching the server (swim_policy.price_for):
  // members pay price_member, non-members price_non_member (falling back to the member price).
  // A non-member adult enrolls via member_id but is_member is false, so key off the flag.
  const selectedSwimmers = familyMembers.filter((f) => selectedKidIds.has(f.id));
  const priceForSwimmer = (s: HouseholdSwimmer) =>
    s.is_member
      ? Number(program.price_member)
      : Number(program.price_non_member ?? program.price_member);
  const unitPrices = selectedSwimmers.map(priceForSwimmer);
  const amountDue = unitPrices.reduce((sum, p) => sum + p, 0);
  // A household is uniformly member or non-member, so the unit price is usually the same;
  // show the "× count" breakdown only when it is.
  const uniformUnitPrice =
    unitPrices.length > 0 && unitPrices.every((p) => p === unitPrices[0]) ? unitPrices[0] : null;

  // Fetch consent metadata once on mount. Small payload, cached at the
  // server, so doing this eagerly costs almost nothing and makes step 2
  // ready as soon as the user reaches it.
  useEffect(() => {
    void (async () => {
      try {
        const list = await consentService.listConsentTypes();
        setConsentMeta(new Map(list.map((c) => [c.key, c])));
      } catch {
        // Metadata is non-blocking. If it fails the wizard falls back to
        // rendering the consent key as a humanized label.
      }
    })();
  }, []);

  useEffect(() => {
    if (step === 0 && familyMembers.length === 0) {
      void loadFamily();
    }
  }, [step]);

  function consentTitle(key: string): string {
    return consentMeta.get(key)?.title || key.replace(/_/g, ' ');
  }

  async function loadFamily() {
    setLoadingFamily(true);
    setError(null);
    try {
      // ONE endpoint for members AND non-members (NM-2 shared layer): it returns the
      // whole household — the caller (is_self) plus their dependents — each already
      // tagged with the kind the enrollment API expects. No more branching on
      // membership_status or manually splicing the caller in. See helper/household.py.
      type SwimmerRow = {
        kind: 'member' | 'member_dependent' | 'non_member' | 'non_member_dependent';
        id: string;
        first_name: string;
        last_name: string;
        date_of_birth: string | null;
        is_member: boolean;
        is_self: boolean;
        member_type: string;
      };
      const res = await axiosInstance.get<{ data: SwimmerRow[] }>('/members/me/swimmers');
      const rows = res.data.data ?? [];
      const out: HouseholdSwimmer[] = [];

      for (const s of rows) {
        const mt = (s.member_type || '').toUpperCase();
        // "Kid" (for audience) == a managed dependent: a DEPENDENT Member child or
        // any NonMemberFamily row. Account holders + SECONDARY co-members are adults.
        const isKid =
          s.kind === 'non_member_dependent' ||
          (s.kind === 'member_dependent' && mt === 'DEPENDENT');

        if (isKid && allowKids) {
          out.push({
            id: s.id,
            first_name: s.first_name,
            last_name: s.last_name,
            date_of_birth: s.date_of_birth,
            member_type: mt || 'DEPENDENT',
            relationship_to_primary: s.is_self ? 'Self' : null,
            is_active: true,
            is_member: s.is_member,
            // A member child enrolls as dependent_id; a NonMemberFamily child as
            // non_member_dependent_id.
            kind: s.kind === 'non_member_dependent' ? 'non_member_dependent' : 'dependent',
          });
        } else if (!isKid && allowAdults) {
          out.push({
            id: s.id,
            first_name: s.first_name,
            last_name: s.last_name,
            date_of_birth: s.date_of_birth,
            member_type: mt || 'PRIMARY',
            relationship_to_primary: s.is_self ? 'Self' : null,
            is_active: true,
            is_member: s.is_member,
            // Adults (the caller, a spouse, or a non-member enrolling themselves) all
            // enroll via member_id — the server prices by membership, not by column.
            kind: 'member',
          });
        }
      }
      setFamilyMembers(out);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load household members');
    } finally {
      setLoadingFamily(false);
    }
  }

  async function submit() {
    if (selectedKidIds.size === 0) return;
    setSubmitting(true);
    setError(null);

    const granted = Object.entries(consentChecks)
      .filter(([, v]) => v)
      .map(([k]) => k);

    const ids = Array.from(selectedKidIds);
    type ErrLike = { response?: { data?: { error?: string } }; message?: string };

    // Sequential — keeps the API's per-request session simple and gives clean
    // per-swimmer result ordering. Slower than Promise.all by N*RTT but N is small.
    const collected: PerKidResult[] = [];
    for (const id of ids) {
      const swimmer = familyMembers.find((f) => f.id === id);
      const swimmerName = swimmer ? `${swimmer.first_name} ${swimmer.last_name}`.trim() || 'Swimmer' : 'Swimmer';
      try {
        const res = await swimService.enrollSwimmer({
          program_id: program.id,
          ...(swimmer?.kind === 'member'
            ? { member_id: id }
            : swimmer?.kind === 'non_member_dependent'
            ? { non_member_dependent_id: Number(id) }
            : { dependent_id: id }),
          granted_consents: granted,
        });
        collected.push({ dependentId: id, swimmerName, status: 'ok', response: res });
      } catch (e) {
        const err = e as ErrLike;
        collected.push({
          dependentId: id,
          swimmerName,
          status: 'error',
          error: err?.response?.data?.error || err?.message || 'Enrollment failed',
        });
      }
    }
    setResults(collected);
    setSubmitting(false);
  }

  const allConsentsChecked =
    requiredConsents.length === 0 || requiredConsents.every((c) => consentChecks[c]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Enroll in {program.name}</DialogTitle>
      <DialogContent dividers>
        {!results && (
          isMobile ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Step {step + 1} of {STEPS.length}
              </Typography>
              <Typography variant="subtitle1">{STEPS[step]}</Typography>
            </Box>
          ) : (
            <Stepper activeStep={step} sx={{ mb: 3 }}>
              {STEPS.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          )
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {results ? (
          (() => {
            const okCount = results.filter((r) => r.status === 'ok').length;
            const errCount = results.length - okCount;
            return (
              <Stack spacing={2}>
                {errCount === 0 && (
                  <Zoom in timeout={450}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                      <CheckCircleOutlineIcon
                        sx={{
                          fontSize: 72,
                          color: 'success.main',
                          animation: 'enrollPop 0.5s ease-out',
                          '@keyframes enrollPop': {
                            '0%': { transform: 'scale(0.4)', opacity: 0 },
                            '60%': { transform: 'scale(1.15)', opacity: 1 },
                            '100%': { transform: 'scale(1)' },
                          },
                        }}
                      />
                    </Box>
                  </Zoom>
                )}
                <Alert severity={errCount === 0 ? 'success' : okCount === 0 ? 'error' : 'warning'}>
                  {errCount === 0
                    ? `Enrolled ${okCount} swimmer${okCount === 1 ? '' : 's'}.`
                    : okCount === 0
                    ? `Could not enroll any swimmer.`
                    : `Enrolled ${okCount} of ${results.length} — ${errCount} failed.`}
                </Alert>
                <Stack spacing={1.5}>
                  {results.map((r) => (
                    <Box
                      key={r.dependentId}
                      sx={{
                        borderLeft: 3,
                        borderColor: r.status === 'ok'
                          ? r.response?.data.status === 'waitlisted' ? 'warning.main' : 'success.main'
                          : 'error.main',
                        pl: 2,
                        py: 0.5,
                      }}
                    >
                      <Typography variant="subtitle2">{r.swimmerName}</Typography>
                      {r.status === 'ok' && r.response ? (
                        <>
                          <Typography variant="body2">
                            {r.response.message}
                            {r.response.data.waitlist_position != null && (
                              <> (position #{r.response.data.waitlist_position})</>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Amount ${Number(r.response.data.amount_due).toFixed(2)}
                            {r.response.missing_consents.length > 0 && (
                              <>
                                {' • '}
                                <Typography component="span" variant="caption" color="warning.main">
                                  missing: {r.response.missing_consents.map((c) => consentTitle(c)).join(', ')}
                                </Typography>
                              </>
                            )}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="error.main">{r.error}</Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Stack>
            );
          })()
        ) : (
          <>
            {step === 0 && (
              <>
                {loadingFamily ? (
                  <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
                ) : familyMembers.length === 0 ? (
                  <Alert severity="warning">
                    No children on your account yet. Add a family member from your profile before enrolling.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Select one or more swimmers to enroll in this class.
                    </Typography>
                    <List dense>
                      {familyMembers.map((f) => {
                        const age = ageFromDob(f.date_of_birth);
                        const checked = selectedKidIds.has(f.id);
                        return (
                          <ListItem key={f.id} disablePadding>
                            <ListItemButton
                              onClick={() => {
                                const next = new Set(selectedKidIds);
                                if (next.has(f.id)) next.delete(f.id);
                                else next.add(f.id);
                                setSelectedKidIds(next);
                              }}
                            >
                              <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                              <ListItemText
                                primary={`${f.first_name} ${f.last_name}`}
                                secondary={age != null ? `Age ${age}` : (f.relationship_to_primary ?? 'Dependent')}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                    {selectedKidIds.size > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {selectedKidIds.size} selected
                      </Typography>
                    )}
                  </>
                )}
              </>
            )}

            {step === 1 && (
              <Stack spacing={1.5}>
                {requiredConsents.length === 0 ? (
                  <Alert severity="info">No consents required for this class.</Alert>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Tap any row to expand the full text. Check the box to confirm you've read and agree.
                      Your typed acceptance + timestamp serves as your signature.
                    </Typography>
                    <Stack
                      divider={<Divider />}
                      sx={{
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      {requiredConsents.map((key) => {
                        const meta = consentMeta.get(key);
                        const title = meta?.title || key.replace(/_/g, ' ');
                        const summary = meta?.summary || '';
                        const checked = !!consentChecks[key];
                        return (
                          <Box
                            key={key}
                            onClick={() =>
                              setConsentChecks((p) => ({ ...p, [key]: !p[key] }))
                            }
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              px: 1.5,
                              py: 1.5,
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                            }}
                          >
                            <Checkbox
                              checked={checked}
                              tabIndex={-1}
                              sx={{ mt: -0.5, p: 0.5 }}
                              inputProps={{ 'aria-label': `Confirm ${title}` }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {title}
                              </Typography>
                              {summary && (
                                <Typography variant="caption" color="text.secondary">
                                  {summary}
                                </Typography>
                              )}
                            </Box>
                            {meta && (
                              <IconButton
                                size="small"
                                aria-label={`Read full text of ${title}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setInfoConsent(meta);
                                }}
                                sx={{ mt: -0.5 }}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Consents are saved to your child's profile and will be recognized on future signups.
                    </Typography>
                  </>
                )}
              </Stack>
            )}

            {step === 2 && (
              <Stack spacing={1.5}>
                <Typography variant="body2">
                  <strong>Class:</strong> {program.name}
                </Typography>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Swimmers ({selectedKidIds.size}):</strong>
                  </Typography>
                  <Stack spacing={0.25} sx={{ pl: 1 }}>
                    {Array.from(selectedKidIds).map((id) => {
                      const k = familyMembers.find((f) => f.id === id);
                      if (!k) return null;
                      return (
                        <Typography key={id} variant="body2">
                          • {k.first_name} {k.last_name}
                        </Typography>
                      );
                    })}
                  </Stack>
                </Box>
                <Typography variant="body2">
                  <strong>Amount due:</strong>{' '}
                  ${amountDue.toFixed(2)}
                  {selectedSwimmers.length > 1 && uniformUnitPrice != null && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}(${uniformUnitPrice.toFixed(2)} × {selectedSwimmers.length})
                    </Typography>
                  )}
                </Typography>
                <Divider />
                <Typography variant="caption" color="text.secondary">
                  Cancellation: contact the club office at least 48 hours before the first session for a full refund.
                  Cancellations within 48 hours are at the club's discretion.
                </Typography>
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
          '& > button': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
        }}
      >
        {results ? (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" onClick={onEnrolled}>Go to My Enrollments</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
            {step > 0 && (
              <Button onClick={() => setStep((s) => s - 1)} disabled={submitting}>Back</Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => setStep((s) => s + 1)}
                disabled={
                  (step === 0 && selectedKidIds.size === 0) ||
                  (step === 1 && !allConsentsChecked)
                }
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => void submit()}
                disabled={submitting || selectedKidIds.size === 0}
              >
                {submitting
                  ? 'Submitting…'
                  : `Submit ${selectedKidIds.size} enrollment${selectedKidIds.size === 1 ? '' : 's'}`}
              </Button>
            )}
          </>
        )}
      </DialogActions>

      {/* Info sheet — slides over the wizard with the full legal text for one
          consent. Closing returns the parent to the consent step where they
          can then check the box. */}
      <Dialog
        open={!!infoConsent}
        onClose={() => setInfoConsent(null)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{infoConsent?.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {infoConsent?.full_text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoConsent(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SwimEnrollmentWizard;
